import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Edit, FileDownload } from '@mui/icons-material';
import { getNegotiationPending, getNegotiationCompleted, exportNegotiationPending, exportNegotiationCompleted, getUsers } from '../services/api';
import { toast } from 'react-toastify';

function NegotiationCost({ user }) {
  const [pendingInfluencers, setPendingInfluencers] = useState([]);
  const [completedInfluencers, setCompletedInfluencers] = useState([]);
  const [inProgressInfluencers, setInProgressInfluencers] = useState([]);
  const [notInterestedInfluencers, setNotInterestedInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [statusData, setStatusData] = useState({ status: '', negotiation_remarks: '' });
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [filteredPendingCount, setFilteredPendingCount] = useState(0);
  const [filteredCompletedCount, setFilteredCompletedCount] = useState(0);
  const [filteredInProgressCount, setFilteredInProgressCount] = useState(0);
  const [filteredNotInterestedCount, setFilteredNotInterestedCount] = useState(0);
  const [totalInstagramSavings, setTotalInstagramSavings] = useState(0);
  const [costData, setCostData] = useState({
    reelCost: '',
    youtubeShortsCoast: '',
    youtubeVideoCost: '',
    remarks: ''
  });
  const [editCompletedModalOpen, setEditCompletedModalOpen] = useState(false);
  const [editCompletedData, setEditCompletedData] = useState({ reelCost: '', remarks: '', originalReelCost: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingInfluencers();
    fetchCompletedInfluencers();
    fetchInProgressInfluencers();
    fetchNotInterestedInfluencers();
    fetchUsers();
  }, []);

  useEffect(() => {
    updateFilteredCounts();
  }, [pendingInfluencers, completedInfluencers, inProgressInfluencers, notInterestedInfluencers, userFilter]);

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data.filter(user => user.role !== 'superadmin' && user.role !== 'super_admin'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const updateFilteredCounts = () => {
    const filteredPending = getFilteredPendingRows();
    const filteredCompleted = getFilteredCompletedRows();
    const filteredInProgress = getFilteredInProgressRows();
    const filteredNotInterested = getFilteredNotInterestedRows();
    setFilteredPendingCount(filteredPending.length);
    setFilteredCompletedCount(filteredCompleted.length);
    setFilteredInProgressCount(filteredInProgress.length);
    setFilteredNotInterestedCount(filteredNotInterested.length);

    const rowscount = filteredCompleted.length || 1;

    const totalSavingsPercentage = filteredCompleted.reduce((sum, row) => {
      const reelCost = Number(row.reelCost) || 0;
      const negotiatedCost = Number(row.negotiatedreelCost) || 0;
      if (reelCost === 0) return sum;
      const savingsPercentage = ((reelCost - negotiatedCost) / reelCost) * 100;
      return sum + savingsPercentage;
    }, 0);

    const averageSavingsPercentage = totalSavingsPercentage / rowscount;
    setTotalInstagramSavings(averageSavingsPercentage);
  };

  const fetchPendingInfluencers = async () => {
    setLoading(true);
    try {
      const response = await getNegotiationPending();
      setPendingInfluencers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch pending influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedInfluencers = async () => {
    try {
      const response = await getNegotiationCompleted();
      setCompletedInfluencers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch completed influencers:', error);
    }
  };

  const fetchInProgressInfluencers = async () => {
    try {
      const { default: api } = await import('../services/api');
      const response = await api.get('/negotiation/in-progress');
      setInProgressInfluencers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch in progress influencers:', error);
    }
  };

  const fetchNotInterestedInfluencers = async () => {
    try {
      const { default: api } = await import('../services/api');
      const response = await api.get('/negotiation/not-interested');
      setNotInterestedInfluencers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch not interested influencers:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'negotiated': return 'success';
      case 'pending': return 'warning';
      case 'in-progress': return 'info';
      case 'not-interested': return 'error';
      default: return 'default';
    }
  };

  const pendingColumns = [
    { field: 'sno', headerName: 'SNO', flex: 0.4, minWidth: 80 },
    { field: 'influencerCode', headerName: 'Influencer ID', width: 100 },
    { field: 'name', headerName: 'Influencer Name', flex: 1, minWidth: 150 },
    { field: 'mobile', headerName: 'Mobile', flex: 1, minWidth: 150 },
    // { field: 'assignedTo', headerName: 'Assigned To', flex: 1, minWidth: 150 },
    {
      field: "assignedTo",
      headerName: 'Assigned To',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const assignedTo = params.row?.onboarderId?.name || 'N/A';
        return assignedTo;
      }
    },
    {
      field: "totalcostinstagram",
      headerName: 'Total Cost Instagram',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.row?.reelCost;
        if (!value || isNaN(value)) return "-";
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "totalcostyoutube",
      headerName: "Total Cost Youtube",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const shorts = params.row?.youtubeShortsCost;
        const video = params.row?.youtubeVideoCost;
        if (!shorts || !video) return "-";
        const total = shorts + video;
        return `₹${total.toLocaleString()}`;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
          sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
          onClick={() => handleStatusClick(params.row)}
        />
      )
    },
    {
      field: 'negotiation_remarks',
      headerName: 'Negotiation Remarks',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.row?.negotiation_remarks || '-'}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => handleEditClick(params.row)}
          // sx={{ color: '#5386e4' }}
          >
            Edit
          </Button>
        </Box>
      )
    }
  ];


  const negotiatedColumns = [
    { field: 'sno', headerName: 'SNO', flex: 0.4, minWidth: 80 },
    { field: 'influencerCode', headerName: 'Influencer ID', width: 100 },
    { field: 'name', headerName: 'Influencer Name', flex: 1, minWidth: 150 },
    { field: 'mobile', headerName: 'Mobile', flex: 1, minWidth: 150 },
    {
      field: "assignedTo",
      headerName: 'Assigned To',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const assignedTo = params.row?.onboarderId?.name || 'N/A';
        return assignedTo;
      }
    },
    {
      field: "totalcostinstagram",
      headerName: 'Total Cost Instagram',
      flex: 1,
      minWidth: 160,
      cellClassName: 'instagram-column',
      renderCell: (params) => {
        const value = params.row?.reelCost;
        if (!value || isNaN(value)) return "-";
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "negotiatedreelCost",
      headerName: 'Negotiated Total (Instagram)',
      flex: 1,
      minWidth: 230,
      cellClassName: 'instagram-column',
      renderCell: (params) => {
        const value = params.row?.negotiatedreelCost;
        if (!value || isNaN(value)) return "-";
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "savingsCostInstagram",
      headerName: 'Savings Cost Instagram',
      flex: 1,
      minWidth: 200,
      cellClassName: 'instagram-column',
      renderCell: (params) => {
        const reelCostVal = params.row?.reelCost;
        const negotiatedVal = params.row?.negotiatedreelCost;

        if (!reelCostVal || isNaN(reelCostVal)) return "-";

        let value = reelCostVal - negotiatedVal;
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "savingsInstagramPercentage",
      headerName: 'Savings Percentage Instagram',
      flex: 1,
      minWidth: 230,
      cellClassName: 'instagram-column',
      renderCell: (params) => {
        const reelCostVal = params.row?.reelCost;
        const negotiatedVal = params.row?.negotiatedreelCost;

        if (!reelCostVal || isNaN(reelCostVal)) return "-";

        let savingsCost = reelCostVal - negotiatedVal;
        let percentage = Math.round((savingsCost / reelCostVal) * 100);
        return `${Number(percentage).toLocaleString()}%`;
      }
    },
    {
      field: "totalcostyoutube",
      headerName: "Total Cost Youtube",
      flex: 1,
      minWidth: 150,
      cellClassName: 'youtube-column',
      renderCell: (params) => {
        const shorts = params.row?.youtubeShortsCost;
        const video = params.row?.youtubeVideoCost;
        if (!shorts || !video) return "-";
        const total = shorts + video;
        return `₹${total.toLocaleString()}`;
      }
    },
    {
      field: "negotiatedyoutube",
      headerName: "Negotiated Total (YouTube)",
      flex: 1,
      minWidth: 230,
      cellClassName: 'youtube-column',
      renderCell: (params) => {
        const shorts = params.row?.negotiatedYoutubeShortsCost;
        const video = params.row?.negotiatedYoutubeVideoCost;
        if (!shorts || !video) return "-";
        const total = shorts + video;
        return `₹${total.toLocaleString()}`;
      }
    },
    {
      field: "savingsCostYoutube",
      headerName: 'Savings Cost YouTube',
      flex: 1,
      minWidth: 200,
      cellClassName: 'youtube-column',
      renderCell: (params) => {
        const shorts = params.row?.youtubeShortsCost;
        const video = params.row?.youtubeVideoCost;
        if (!shorts || !video) return "-";
        const originalTotal = shorts + video;

        const negotiatedShorts = params.row?.negotiatedYoutubeShortsCost;
        const negotiatedVideo = params.row?.negotiatedYoutubeVideoCost;
        if (!negotiatedShorts || !negotiatedVideo) return "-";
        const negotiatedTotal = negotiatedShorts + negotiatedVideo;

        let value = originalTotal - negotiatedTotal;
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "savingsPercentageYoutube",
      headerName: 'Savings Percentage YouTube',
      flex: 1,
      minWidth: 230,
      cellClassName: 'youtube-column',
      renderCell: (params) => {
        const shorts = params.row?.youtubeShortsCost;
        const video = params.row?.youtubeVideoCost;
        if (!shorts || !video) return "-";
        const originalTotal = shorts + video;

        const negotiatedShorts = params.row?.negotiatedYoutubeShortsCost;
        const negotiatedVideo = params.row?.negotiatedYoutubeVideoCost;
        if (!negotiatedShorts || !negotiatedVideo) return "-";
        const negotiatedTotal = negotiatedShorts + negotiatedVideo;

        let SavingsValue = originalTotal - negotiatedTotal;
        let percentage = Math.round((SavingsValue / originalTotal) * 100);
        return `${Number(percentage).toLocaleString()}%`;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      )
    },
    {
      field: 'remarks',
      headerName: 'Negotiation Remarks',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.row?.remarks || '-'}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={() => navigate(`/influencer/${params.row._id}`)}
          >
            View
          </Button>
          
          {user.role === 'admin' && (
            <Button
              size="small"
              startIcon={<Edit />}
              onClick={() => handleEditCompletedClick(params.row)}
            >
              Edit
            </Button>
          )}
        </Box>
      )
    }
  ];

  const getFilteredPendingRows = () => {
    return pendingInfluencers
      .filter(inf => !userFilter || (inf.onboarderId && inf.onboarderId._id === userFilter))
      .map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        mobile: influencer.contactNumber,
        platformType: influencer.platformType,
        reelCost: influencer.reelCost,
        youtubeVideoCost: influencer.youtubeVideoCost,
        youtubeShortsCost: influencer.youtubeShortsCost,
        category: influencer.category,
        status: 'pending',
        negotiation_remarks: influencer.negotiation_remarks,
        onboarderId: influencer.onboarderId,
        _id: influencer._id
      }));
  };

  const getFilteredCompletedRows = () => {
    return completedInfluencers
      .filter(inf => !userFilter || (inf.onboarderId && inf.onboarderId._id === userFilter))
      .map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        mobile: influencer.contactNumber,
        platformType: influencer.platformType,
        reelCost: influencer?.reelCost,
        youtubeVideoCost: influencer?.youtubeVideoCost,
        youtubeShortsCost: influencer?.youtubeShortsCost,
        negotiatedreelCost: influencer.negotiationCostData?.reelCost,
        negotiatedYoutubeVideoCost: influencer.negotiationCostData?.youtubeVideoCost,
        negotiatedYoutubeShortsCost: influencer.negotiationCostData?.youtubeShortsCoast,
        remarks: influencer?.negotiationCostData?.remarks,
        category: influencer.category,
        cost: 'Negotiated',
        status: 'negotiated',
        onboarderId: influencer.onboarderId,
        _id: influencer._id
      }));
  };

  const getFilteredInProgressRows = () => {
    return inProgressInfluencers
      .filter(inf => !userFilter || (inf.onboarderId && inf.onboarderId._id === userFilter))
      .map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        mobile: influencer.contactNumber,
        platformType: influencer.platformType,
        reelCost: influencer.reelCost,
        youtubeVideoCost: influencer.youtubeVideoCost,
        youtubeShortsCost: influencer.youtubeShortsCost,
        category: influencer.category,
        status: 'in-progress',
        negotiation_remarks: influencer.negotiation_remarks,
        onboarderId: influencer.onboarderId,
        _id: influencer._id
      }));
  };

  const getFilteredNotInterestedRows = () => {
    return notInterestedInfluencers
      .filter(inf => !userFilter || (inf.onboarderId && inf.onboarderId._id === userFilter))
      .map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        mobile: influencer.contactNumber,
        platformType: influencer.platformType,
        reelCost: influencer.reelCost,
        youtubeVideoCost: influencer.youtubeVideoCost,
        youtubeShortsCost: influencer.youtubeShortsCost,
        category: influencer.category,
        status: 'not-interested',
        negotiation_remarks: influencer.negotiation_remarks,
        onboarderId: influencer.onboarderId,
        _id: influencer._id
      }));
  };

  const handleStatusClick = (influencer) => {
    setSelectedInfluencer(influencer);
    setStatusData({ status: influencer.status, negotiation_remarks: influencer.negotiation_remarks || '' });
    setStatusModalOpen(true);
  };

  const handleStatusUpdate = async () => {
    try {
      const { default: api } = await import('../services/api');
      await api.put(`/negotiation/${selectedInfluencer._id}/status`, statusData);
      setStatusModalOpen(false);
      fetchPendingInfluencers();
      fetchCompletedInfluencers();
      fetchInProgressInfluencers();
      fetchNotInterestedInfluencers();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditClick = (influencer) => {
    setSelectedInfluencer(influencer);
    setCostData({
      reelCost: '',
      youtubeShortsCoast: '',
      youtubeVideoCost: '',
      remarks: ''
    });
    setEditModalOpen(true);
  };

  const handleSaveCost = async () => {
    try {
      const { default: api } = await import('../services/api');
      await api.post('/negotiation-cost', {
        influencerId: selectedInfluencer._id,
        ...costData
      });
      setEditModalOpen(false);
      fetchPendingInfluencers();
      fetchCompletedInfluencers();
      fetchInProgressInfluencers();
      fetchNotInterestedInfluencers();
    } catch (error) {
      console.error('Failed to save negotiation cost:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setCostData(prev => ({ ...prev, [field]: value }));
  };

  const getReelCostError = () => {
    if (!costData.reelCost || !selectedInfluencer?.reelCost) return '';
    if (Number(costData.reelCost) > Number(selectedInfluencer.reelCost)) {
      return `Must be ≤ ₹${Number(selectedInfluencer.reelCost).toLocaleString()}`;
    }
    return '';
  };

  const handleEditCompletedClick = (row) => {
    setSelectedInfluencer(row);
    setEditCompletedData({
      originalReelCost: row.reelCost || '',
      reelCost: row.negotiatedreelCost || '',
      remarks: row.remarks || ''
    });
    setEditCompletedModalOpen(true);
  };

  const handleUpdateCompletedCost = async () => {
    try {
      const { default: api } = await import('../services/api');
      await api.put(`/negotiation-cost/${selectedInfluencer._id}`, editCompletedData);
      setEditCompletedModalOpen(false);
      toast.success('Cost updated successfully');
      fetchCompletedInfluencers();
    } catch (error) {
      console.error('Failed to update cost:', error);
      toast.error('Failed to update cost');
    }
  };

  const handleExportPending = async () => {
    try {
      const { default: api } = await import('../services/api');
      const params = userFilter ? { userId: userFilter } : {};
      const response = await api.get('/negotiation/export/pending', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'pending_negotiations.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export pending negotiations:', error);
    }
  };

  const handleExportCompleted = async () => {
    try {
      const { default: api } = await import('../services/api');
      const params = userFilter ? { userId: userFilter } : {};
      const response = await api.get('/negotiation/export/completed', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'completed_negotiations.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export completed negotiations:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
        Negotiation Cost Management
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#fff3e0',
            height: '83px',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#ffe0b2' }
          }}
          onClick={() => setActiveTab(0)}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
              {filteredPendingCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Pending
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#fff3e0',
            height: '83px',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#ffe0b2' }
          }}
          onClick={() => setActiveTab(1)}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
              {filteredInProgressCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              In Progress
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#ffebee',
            height: '83px',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#ffcdd2' }
          }}
          onClick={() => setActiveTab(2)}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
              {filteredNotInterestedCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Not Interested
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#e8f5e8',
            height: '83px',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#c8e6c8' }
          }}
          onClick={() => setActiveTab(3)}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              {filteredCompletedCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Completed
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#e3f2fd',
            height: '83px'
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {Math.round(totalInstagramSavings)}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Avg Savings %
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        {user.role == 'admin' && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by User</InputLabel>
            <Select
              value={userFilter}
              label="Filter by User"
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <MenuItem value="">All Users</MenuItem>
              {users.filter(user => user.role === 'onboarder' || user.role === 'admin').map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name || user.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={activeTab === 0 ? handleExportPending : activeTab === 3 ? handleExportCompleted : () => { }}
        // sx={{ bgcolor: '#5386e4' }}
        >
          Export {activeTab === 0 ? 'Pending' : activeTab === 1 ? 'In Progress' : activeTab === 2 ? 'Not Interested' : 'Completed'}
        </Button>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label={`Pending (${filteredPendingCount})`} />
        <Tab label={`In Progress (${filteredInProgressCount})`} />
        <Tab label={`Not Interested (${filteredNotInterestedCount})`} />
        <Tab label={`Completed (${filteredCompletedCount})`} />
      </Tabs>

      {activeTab === 0 && (
        <DataGrid
          columns={pendingColumns}
          rows={loading ? [] : getFilteredPendingRows()}
          loading={loading}
          pageSize={10}
          disableRowSelectionOnClick
          autoHeight
          className="table_border"
          sx={{
            boxShadow: 2,
            backgroundColor: 'white',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa'
            },
            '& .MuiDataGrid-iconSeparator': {
              display: 'none'
            },
            '& .MuiDataGrid-row': {
              backgroundColor: 'white',
              borderLeft: 'solid 1px #e5e7eb',
              borderRight: 'solid 1px #e5e7eb'
            }
          }}
        />
      )}

      {activeTab === 1 && (
        <DataGrid
          columns={pendingColumns}
          rows={loading ? [] : getFilteredInProgressRows()}
          loading={loading}
          pageSize={10}
          disableRowSelectionOnClick
          autoHeight
          className="table_border"
          sx={{
            boxShadow: 2,
            backgroundColor: 'white',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa'
            },
            '& .MuiDataGrid-iconSeparator': {
              display: 'none'
            },
            '& .MuiDataGrid-row': {
              backgroundColor: 'white',
              borderLeft: 'solid 1px #e5e7eb',
              borderRight: 'solid 1px #e5e7eb'
            }
          }}
        />
      )}

      {activeTab === 2 && (
        <DataGrid
          columns={pendingColumns}
          rows={loading ? [] : getFilteredNotInterestedRows()}
          loading={loading}
          pageSize={10}
          disableRowSelectionOnClick
          autoHeight
          className="table_border"
          sx={{
            boxShadow: 2,
            backgroundColor: 'white',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa'
            },
            '& .MuiDataGrid-iconSeparator': {
              display: 'none'
            },
            '& .MuiDataGrid-row': {
              backgroundColor: 'white',
              borderLeft: 'solid 1px #e5e7eb',
              borderRight: 'solid 1px #e5e7eb'
            }
          }}
        />
      )}

      {activeTab === 3 && (
        <DataGrid
          columns={negotiatedColumns}
          rows={loading ? [] : getFilteredCompletedRows()}
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20]}
          disableRowSelectionOnClick
          autoHeight
          className="table_border"
          sx={{
            boxShadow: 2,
            backgroundColor: 'white',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa'
            },
            '& .MuiDataGrid-iconSeparator': {
              display: 'none'
            },
            '& .MuiDataGrid-row': {
              backgroundColor: 'white',
              borderLeft: 'solid 1px #e5e7eb',
              borderRight: 'solid 1px #e5e7eb'
            }
          }}
        />
      )}

      {/* Status Update Modal */}
      <Dialog open={statusModalOpen} onClose={() => setStatusModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Status - {selectedInfluencer?.name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusData.status}
              label="Status"
              onChange={(e) => setStatusData(prev => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in-progress">In Progress</MenuItem>
              <MenuItem value="not-interested">Not Interested</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Remarks (Optional)"
            multiline
            rows={3}
            value={statusData.negotiation_remarks}
            onChange={(e) => setStatusData(prev => ({ ...prev, negotiation_remarks: e.target.value }))}
            placeholder="Enter any remarks..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusModalOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Completed Cost Modal */}
      <Dialog open={editCompletedModalOpen} onClose={() => setEditCompletedModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Cost - {selectedInfluencer?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Total Cost Instagram"
            type="number"
            value={editCompletedData.originalReelCost}
            onChange={(e) => setEditCompletedData(prev => ({ ...prev, originalReelCost: e.target.value }))}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Negotiated Reel Cost"
            type="number"
            value={editCompletedData.reelCost}
            onChange={(e) => setEditCompletedData(prev => ({ ...prev, reelCost: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={3}
            value={editCompletedData.remarks}
            onChange={(e) => setEditCompletedData(prev => ({ ...prev, remarks: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCompletedModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateCompletedCost} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Cost Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Negotiation Cost - {selectedInfluencer?.name}</DialogTitle>
        <DialogContent>
          {selectedInfluencer?.reelCost ? (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 0.5 }}>
                Total Cost Instagram
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                ₹{Number(selectedInfluencer.reelCost).toLocaleString()}
              </Typography>
              <TextField
                fullWidth
                label="Reel Cost"
                type="number"
                value={costData.reelCost}
                onChange={(e) => handleInputChange('reelCost', e.target.value)}
                error={!!getReelCostError()}
                helperText={getReelCostError()}
                sx={{ mb: 2 }}
              />
            </>
          ) : null}
          {(selectedInfluencer?.youtubeShortsCost && selectedInfluencer?.youtubeVideoCost) ? (
            <>
              <TextField
                fullWidth
                label="YouTube Shorts Cost"
                type="number"
                value={costData.youtubeShortsCoast}
                onChange={(e) => handleInputChange('youtubeShortsCoast', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="YouTube Video Cost"
                type="number"
                value={costData.youtubeVideoCost}
                onChange={(e) => handleInputChange('youtubeVideoCost', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          ) : null}
          <TextField
            fullWidth
            label="Additional Remarks (Optional)"
            multiline
            rows={3}
            value={costData.remarks}
            onChange={(e) => handleInputChange('remarks', e.target.value)}
            placeholder="Enter any additional remarks..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCost} variant="contained" disabled={!!getReelCostError()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NegotiationCost;